import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';
import { companyInfo } from '../../mock';
import { auth } from '../../lib/firebaseClient';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
} from 'firebase/auth';
import { toast } from 'sonner';

const Auth = () => {
  const [mode, setMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Check if there's a redirect parameter
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get('redirect') || '/';
        navigate(redirect);
      }
    });
    return () => unsub();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'signup') {
        await createUserWithEmailAndPassword(auth, email, password);
        toast.success('Account Created!', {
          description: 'Welcome to NT Studios',
        });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        toast.success('Welcome Back!', {
          description: 'Successfully logged in',
        });
      }
      
      // Redirect to home or specified page
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect') || '/';
      navigate(redirect);
    } catch (error) {
      toast.error('Authentication Error', {
        description: error.message || 'Failed to authenticate. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-dark)', position: 'relative' }}>
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ zIndex: 0 }}>
        <div
          className="absolute top-20 left-20 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(9, 166, 163, 0.3), transparent)' }}
        />
        <div
          className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(109, 90, 137, 0.3), transparent)' }}
        />
      </div>

      <div className="container mx-auto px-6 relative" style={{ zIndex: 10 }}>
        <div className="max-w-md mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center mb-8">
            <img src={companyInfo.logo} alt={companyInfo.name} className="h-16 w-auto" />
          </Link>

          {/* Card */}
          <div
            className="rounded-2xl p-8"
            style={{
              background: 'rgba(109, 90, 137, 0.1)',
              border: '1px solid rgba(109, 90, 137, 0.3)',
            }}
          >
            {/* Title */}
            <h1 className="text-3xl font-bold text-white mb-2 text-center">
              {mode === 'signup' ? 'Create Account' : 'Welcome Back'}
            </h1>
            <p className="text-neutral-silver text-center mb-8">
              {mode === 'signup'
                ? 'Sign up to access AI-powered tools'
                : 'Sign in to continue to your tools'}
            </p>

            {/* Mode Toggle */}
            <div className="flex gap-2 mb-8">
              <button
                onClick={() => setMode('signup')}
                className={`flex-1 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  mode === 'signup'
                    ? 'bg-accent-teal text-white'
                    : 'bg-transparent text-neutral-silver border border-neutral-silver/20 hover:border-accent-teal hover:text-accent-teal'
                }`}
              >
                Sign Up
              </button>
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  mode === 'login'
                    ? 'bg-accent-teal text-white'
                    : 'bg-transparent text-neutral-silver border border-neutral-silver/20 hover:border-accent-teal hover:text-accent-teal'
                }`}
              >
                Sign In
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <label className="block text-white font-medium mb-2 flex items-center gap-2">
                  <Mail size={18} className="text-accent-teal" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  style={{
                    backgroundColor: 'rgba(109, 90, 137, 0.1)',
                    borderColor: 'rgba(109, 90, 137, 0.3)',
                    color: 'white',
                  }}
                  className="w-full px-4 py-3 rounded-lg border text-white placeholder-neutral-silver/50 focus:outline-none focus:border-accent-teal transition-colors relative z-10"
                  placeholder="you@example.com"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-white font-medium mb-2 flex items-center gap-2">
                  <Lock size={18} className="text-accent-teal" />
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength="6"
                  autoComplete="current-password"
                  style={{
                    backgroundColor: 'rgba(109, 90, 137, 0.1)',
                    borderColor: 'rgba(109, 90, 137, 0.3)',
                    color: 'white',
                  }}
                  className="w-full px-4 py-3 rounded-lg border text-white placeholder-neutral-silver/50 focus:outline-none focus:border-accent-teal transition-colors relative z-10"
                  placeholder="At least 6 characters"
                />
                {mode === 'signup' && (
                  <p className="text-xs text-neutral-silver mt-1">Minimum 6 characters required</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-lg bg-accent-teal text-white font-semibold hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span>Please wait...</span>
                ) : (
                  <>
                    <span>{mode === 'signup' ? 'Create Account' : 'Sign In'}</span>
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>

            {/* Additional Info */}
            <div className="mt-6 text-center text-sm text-neutral-silver">
              {mode === 'signup' ? (
                <p>
                  By signing up, you agree to our{' '}
                  <span className="text-accent-teal cursor-pointer hover:underline">Terms of Service</span>
                </p>
              ) : (
                <p>
                  Don't have an account?{' '}
                  <button onClick={() => setMode('signup')} className="text-accent-teal hover:underline">
                    Sign up
                  </button>
                </p>
              )}
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-neutral-charcoal border-2 border-accent-teal/30 text-accent-teal rounded-lg hover:bg-accent-teal/10 hover:border-accent-teal transition-all duration-300 font-medium"
            >
              <span>←</span>
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
